import { Button, styled } from "@mui/material";

/**
 * The two buttons that take money, defined once (EB-556).
 *
 * They started inside `PurchaseActions` and stayed there through EB-538, which meant the cart
 * drawer — the other place a shopper decides to pay — grew its own vocabulary: white outlined
 * buttons with an `info` border that appears nowhere else in the purchase path. Two surfaces
 * asking the same question looked like two different shops.
 *
 * Shared as `styled` components rather than as theme variants because the shape is the whole
 * point: a caller that has to remember `borderRadius: 999` is a caller that will eventually
 * forget it.
 */
export const PurchaseButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 700,
  // Both fills are light; white text on them fails contrast, so the ink is fixed here rather than
  // left to MUI's automatic contrast text (EB-536).
  color: theme.palette.text.primary,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
  // The fills below are set on the root, which outranks MUI's own disabled rule — without this a
  // disabled button keeps its full-strength orange and only the label greys out.
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

/** Yellow: the reversible choice — the cart keeps the product, the shopper keeps browsing. */
export const AddToCartAction = styled(PurchaseButton)(({ theme }) => ({
  backgroundColor: theme.palette.purchase.cart,
  "&:hover": { backgroundColor: theme.palette.purchase.cartHover },
}));

/** Orange: the one that moves the shopper to checkout, on the detail page and in the cart alike. */
export const BuyNowAction = styled(PurchaseButton)(({ theme }) => ({
  backgroundColor: theme.palette.purchase.buy,
  "&:hover": { backgroundColor: theme.palette.purchase.buyHover },
}));
